import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { getStoredAccessToken } from "@/shared/utils/auth";
import { getPhoneVerificationState } from "@/shared/utils/phoneVerification";

type ProtectedRouteProps = {
  children?: ReactNode;
  redirectTo?: string;
  allowUnverifiedAccess?: boolean;
};

const ProtectedRoute = ({
  children,
  redirectTo = "/sign-in",
  allowUnverifiedAccess = false,
}: ProtectedRouteProps) => {
  const guardsDisabled =
    import.meta.env.VITE_DISABLE_GUARDS?.toString().toLowerCase() === "true";
  const location = useLocation();
  const token = getStoredAccessToken();

  if (guardsDisabled) {
    return children ? <>{children}</> : <Outlet />;
  }

  if (!token) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to={redirectTo} replace state={{ from }} />;
  }

  if (!allowUnverifiedAccess) {
    const { verified, phoneNumber } = getPhoneVerificationState();

    if (!verified) {
      const from = `${location.pathname}${location.search}`;
      const params = new URLSearchParams();
      if (phoneNumber) {
        params.set("phone", phoneNumber);
      }
      if (from && !from.startsWith("/verify-phone")) {
        params.set("redirectTo", from);
      }

      const target = params.toString()
        ? `/verify-phone?${params.toString()}`
        : "/verify-phone";

      return (
        <Navigate
          to={target}
          replace
          state={{
            phoneNumber: phoneNumber ?? "",
            redirectTo: from,
          }}
        />
      );
    }
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
